import type { Address, Basket, Item, Slot } from '~/types/basket'
import type { Order } from '~/types/order'
import type { Variant } from '~/types/product'

/**
 * Provides basket management and related API calls for the shopping cart.
 * Handles basket state, cookies, and communication with backend endpoints.
 */

const RELOAD_BASKET_KEY = 'reload-basket'
const channel = new BroadcastChannel('basket_broadcast_channel')

export function useBasketService() {
  const basketGuidKey = 'basket-guid'
  const basketCountKey = 'basket-count'
  const basketPriceKey = 'basket-price'
  const isFuneralKey = 'basket-is-funeral'

  channel.onmessage = (event) => {
    if (event.data === RELOAD_BASKET_KEY) {
      const nuxtApp = useNuxtApp()
      nuxtApp.runWithContext(() => {
        const queryClient = useQueryClient()
        queryClient.refetchQueries({
          queryKey: ['basket'],
        })
        loadBasket()
      })
    }
  }

  const guidCookie = useCookie(basketGuidKey, { sameSite: true, maxAge: 3153600000, secure: true })
  const countCookie = useCookie(basketCountKey, {
    sameSite: true,
    maxAge: 3153600000,
    secure: true,
  })
  const priceCookie = useCookie(basketPriceKey, {
    sameSite: true,
    maxAge: 3153600000,
    secure: true,
  })
  const isFuneralCookie = useCookie(isFuneralKey, { sameSite: true, maxAge: 3153600000 })
  const basket = useState<{
    guid: null | string
    count: number
    price: number
    isFuneral: boolean
  }>('basket', () => {
    return {
      guid: guidCookie?.value || null,
      count: countCookie?.value ? Number(countCookie.value) : 0,
      price: priceCookie?.value ? Number(priceCookie.value) : 0,
      isFuneral: isFuneralCookie?.value ? Boolean(isFuneralCookie.value) : false,
    }
  })

  /**
   * Loads basket state from cookies into the reactive basket state.
   * @returns {void}
   */
  const loadBasket = (): void => {
    basket.value = {
      guid: guidCookie?.value || null,
      count: countCookie?.value ? Number(countCookie.value) : 0,
      price: priceCookie?.value ? Number(priceCookie.value) : 0,
      isFuneral: isFuneralCookie?.value ? Boolean(isFuneralCookie.value) : false,
    }
  }

  /**
   * Sets the basket state and updates cookies.
   * @param {string} guid - Basket GUID
   * @param {number} count - Number of items in the basket
   * @param {number} price - Total price of the basket
   * @param {boolean} [isFuneral=false] - Whether the basket is for a funeral order
   * @returns {void}
   */
  const setBasket = (
    guid: string,
    count: number,
    price: number,
    isFuneral: boolean = false
  ): void => {
    guidCookie.value = guid
    basket.value.guid = guid
    if (count || count === 0) {
      countCookie.value = count.toString()
      basket.value.count = count
    }
    if (count || count === 0) {
      priceCookie.value = price.toString()
      basket.value.price = price
    }
    isFuneralCookie.value = isFuneral.toString()
    basket.value.isFuneral = isFuneral
  }

  /**
   * Adds products to the basket. Creates a new basket if none exists.
   * @param {{ [id: number]: Variant }} variants - Object of product variants to add
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>} The updated basket
   */
  const addToBasket = async (
    variants: { [id: number]: Variant },
    query?: Record<string, unknown>
  ): Promise<Basket> => {
    const items = Object.values(variants).map((variant) => ({
      product: variant.id,
      quantity: variant?.count ?? 1,
      services: variant.surcharges
        ? Object.keys(variant.surcharges).map((id) => ({
            product: Number(id),
            quantity: variant?.count ?? 1,
            productProduct:
              variant.surcharge.find((sur) => sur.id == Number(id))?.productProduct ?? null,
          }))
        : [],
    }))

    if (!basket.value.guid) {
      // Vytvoříme košík
      try {
        const { data } = await useCustomFetch<{ data: Basket }>({
          url: '/public/baskets',
          query,
          clientOnly: true,
          options: {
            method: 'post',
            body: { items },
          },
        })
        basket.value.guid = data.guid as string
        guidCookie.value = data.guid
        basket.value.count = data.countItems as number
        countCookie.value = data.countItems.toString()
        basket.value.price = data.totalPriceVat.amount as number
        priceCookie.value = data.totalPriceVat.amount.toString()
        basket.value.isFuneral = data.isFuneral ?? false
        isFuneralCookie.value = data.isFuneral?.toString() || 'false'
        nextTick(() => {
          channel.postMessage(RELOAD_BASKET_KEY)
        })
        return data
      } catch (e) {
        throw e
      }
    } else {
      // Vložit do už existujícího košíku
      try {
        const { data } = await useCustomFetch<{ data: Basket }>({
          url: `/public/baskets/${basket.value.guid}`,
          query,
          clientOnly: true,
          options: {
            method: 'patch',
            body: { items, increase: true },
          },
        })
        basket.value.count = data.countItems as number
        countCookie.value = data.countItems.toString()
        basket.value.price = data.totalPriceVat.amount as number
        priceCookie.value = data.totalPriceVat.amount.toString()
        basket.value.isFuneral = data.isFuneral ?? false
        isFuneralCookie.value = data.isFuneral?.toString() || 'false'
        nextTick(() => {
          channel.postMessage(RELOAD_BASKET_KEY)
        })
        return data
      } catch (e) {
        throw e
      }
    }
  }

  /**
   * Changes the basket's items or note.
   * @param {Item[]} [items] - Optional array of items to set in the basket
   * @param {string | null} [note] - Optional note for the basket
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>} The updated basket
   */
  const changeBasket = async (
    items?: Item[],
    note?: string | null,
    query?: Record<string, unknown>
  ): Promise<Basket> => {
    const body: { items?: Item[]; note?: string | null; increase: boolean } = {
      items: undefined,
      increase: false,
      note: undefined,
    }
    if (items) {
      body.items = items
    } else {
      delete body.items
    }
    if (note !== undefined) {
      body.note = note
    } else {
      delete body.note
    }
    try {
      const { data } = await useCustomFetch<{ data: Basket }>({
        url: `/public/baskets/${basket.value.guid}`,
        query,
        clientOnly: true,
        options: {
          method: 'patch',
          body,
        },
      })
      basket.value.count = data.countItems as number
      countCookie.value = data.countItems.toString()
      basket.value.price = data.totalPriceVat.amount as number
      priceCookie.value = data.totalPriceVat.amount.toString()
      basket.value.isFuneral = data.isFuneral ?? false
      isFuneralCookie.value = data.isFuneral?.toString() || 'false'
      nextTick(() => {
        channel.postMessage(RELOAD_BASKET_KEY)
      })
      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Updates the basket's identification information.
   * @param {string} personal - Personal identification
   * @param {string} tax - Tax identification
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>}
   */
  const changeIdentification = async (
    personal: string,
    tax: string,
    query?: Record<string, unknown>
  ): Promise<Basket> => {
    try {
      const { data } = await useCustomFetch<{ data: Basket }>({
        url: `/public/baskets/${basket.value.guid}`,
        query,
        clientOnly: true,
        options: {
          method: 'patch',
          body: {
            personalIdentification: personal,
            taxIdentification: tax,
          },
        },
      })
      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Applies a coupon code to the basket.
   * @param {string} code - Coupon code
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>}
   */
  const applyCoupon = async (code: string, query?: Record<string, unknown>): Promise<Basket> => {
    try {
      const { data } = await useCustomFetch<{ data: Basket }>({
        url: `/public/baskets/${basket.value.guid}/coupons/${code}`,
        query,
        clientOnly: true,
        options: {
          method: 'post',
          body: {},
        },
      })
      basket.value.price = data.totalPriceVat.amount as number
      priceCookie.value = data.totalPriceVat.amount.toString()
      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Removes a coupon code from the basket.
   * @param {string} code - Coupon code
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>}
   */
  const removeCoupon = async (code: string, query?: Record<string, unknown>): Promise<Basket> => {
    try {
      const { data } = await useCustomFetch<{ data: Basket }>({
        url: `/public/baskets/${basket.value.guid}/coupons/${code}`,
        query,
        clientOnly: true,
        options: {
          method: 'delete',
          body: {},
        },
      })
      basket.value.price = data.totalPriceVat.amount as number
      priceCookie.value = data.totalPriceVat.amount.toString()
      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Changes the delivery address for the basket.
   * @param {Address} address - Delivery address
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>}
   */
  const changeAddress = async (
    address: Address,
    query?: Record<string, unknown>
  ): Promise<Basket> => {
    try {
      const { data } = await useCustomFetch<{ data: Basket }>({
        url: `/public/baskets/${basket.value.guid}/addresses`,
        query,
        clientOnly: true,
        options: {
          method: 'put',
          body: {
            address,
          },
          watch: false,
        },
      })
      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Saves arbitrary data to the basket.
   * @param {{ [key: string]: string | number | boolean | null }} value - Data to save
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>}
   */
  const saveData = async (
    value: { [key: string]: string | number | boolean | null },
    query?: Record<string, unknown>
  ): Promise<Basket> => {
    try {
      const { data } = await useCustomFetch<{ data: Basket }>({
        url: `/public/baskets/${basket.value.guid}/data`,
        query,
        clientOnly: true,
        options: {
          method: 'put',
          body: {
            data: value,
          },
        },
      })
      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Saves the selected delivery slot to the basket.
   * @param {Slot | null} delivery - Delivery slot
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>}
   */
  const saveDelivery = async (
    delivery: Slot | null,
    query?: Record<string, unknown>
  ): Promise<Basket> => {
    try {
      const { data } = await useCustomFetch<{ data: Basket }>({
        url: `/public/baskets/${basket.value.guid}/deliveries`,
        query,
        clientOnly: true,
        options: {
          method: 'put',
          body: {
            ...delivery,
          },
        },
      })
      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Saves the selected payment method to the basket.
   * @param {string} payment - Payment method
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<Basket>}
   */
  const savePayment = async (payment: string, query?: Record<string, unknown>): Promise<Basket> => {
    try {
      const { data } = await useCustomFetch<{ data: Basket }>({
        url: `/public/baskets/${basket.value.guid}/payments`,
        query,
        clientOnly: true,
        options: {
          method: 'put',
          body: {
            payment,
          },
        },
      })
      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Removes the basket and clears all related state and cookies.
   * @returns {Promise<void>}
   */
  const removeBasket = async (): Promise<void> => {
    try {
      await useCustomFetch({
        url: `/public/baskets/${basket.value.guid}`,
        clientOnly: true,
        options: {
          method: 'delete',
        },
      })
      clear()
      nextTick(() => {
        channel.postMessage(RELOAD_BASKET_KEY)
      })
    } catch (e) {
      throw e
    }
  }

  /**
   * Finalizes the basket and creates an order.
   * @param {boolean} agree - Whether the user agrees to terms
   * @param {boolean} marketingConsent - Whether the user consents to marketing
   * @param {{ phone: string; email: string }} buyer - Buyer contact information
   * @param {boolean} [saveInvoiceAddress=false] - Save invoice address
   * @param {boolean} [createAccount=false] - Create account from order
   * @param {string[]} [trackingTags] - Traffic-source attribution tags for the order
   * @param {Record<string, unknown>} [query] - Optional query parameters
   * @returns {Promise<{ onlinePayment: boolean, redirect: boolean, redirectUri: string | null, article: { value: string }, guid: string, order: Order }>}
   */
  const finish = async (
    agree: boolean,
    marketingConsent: boolean,
    buyer: { phone: string; email: string },
    saveInvoiceAddress: boolean = false,
    createAccount: boolean = false,
    trackingTags?: string[],
    query?: Record<string, unknown>
  ): Promise<{
    onlinePayment: boolean
    redirect: boolean
    redirectUri: string | null
    article: { value: string }
    guid: string
    order: Order
  }> => {
    try {
      const { data } = await useCustomFetch<{
        data: {
          onlinePayment: boolean
          redirect: boolean
          redirectUri: string | null
          article: { value: string }
          guid: string
          order: Order
        }
      }>({
        url: `/public/baskets/${basket.value.guid}/actions`,
        query,
        clientOnly: true,
        options: {
          method: 'post',
          body: {
            type: 'createOrder',
            agree,
            marketingConsent,
            buyer,
            saveInvoiceAddress,
            createAccount,
            ...(trackingTags?.length ? { trackingTags } : {}),
          },
        },
      })

      return data
    } catch (e) {
      throw e
    }
  }

  /**
   * Clears the basket state and all related cookies and state variables.
   * @returns {void}
   */
  const clear = (): void => {
    basket.value = {
      guid: null,
      count: 0,
      price: 0,
      isFuneral: false,
    }
    guidCookie.value = null
    countCookie.value = '0'
    priceCookie.value = '0'
    isFuneralCookie.value = 'false'
    useCookie('save-delivery-address', {
      sameSite: true,
      maxAge: 3153600000,
      default: () => false,
    }).value = false
    useCookie('create-account-from-order', {
      sameSite: true,
      maxAge: 3153600000,
      default: () => false,
    }).value = false
    useState<Address>('basket-delivery-address').value = { type: 'delivery' }
    useState<Address>('basket-invoice-address').value = { type: 'invoice' }
    useState('basket-buy-as-company').value = false
    useState('basket-same-as-delivery').value = false
    useState('basket-tax-id').value = undefined
    useState('basket-personal-id').value = undefined
    useState('basket-selected-branch').value = undefined
  }

  return {
    basket,
    setBasket,
    addToBasket,
    loadBasket,
    changeBasket,
    changeIdentification,
    applyCoupon,
    removeCoupon,
    changeAddress,
    saveData,
    saveDelivery,
    savePayment,
    removeBasket,
    finish,
    clear,
  }
}
